"use client";

import * as React from "react";

interface FadeInContextType {
	isFadeInComplete: boolean;
}

const FadeInContext = React.createContext<FadeInContextType>({
	isFadeInComplete: true,
});

function intersectsViewport(rect: DOMRect): boolean {
	return (
		rect.bottom > 0 &&
		rect.top < window.innerHeight &&
		rect.right > 0 &&
		rect.left < window.innerWidth
	);
}

export interface FadeInOnScrollProps {
	children: React.ReactNode;
	className?: string;
	/** Optional ARIA role for the outer wrapper (e.g. `listitem`). */
	role?: React.AriaRole;
	delay?: number;
	duration?: number;
	onFadeInComplete?: () => void;
	/**
	 * Skip scroll / intersection gating — render visible immediately (e.g. fixed bottom UI
	 * that must not wait for `IntersectionObserver`).
	 */
	immediate?: boolean;
	/** Passed to IntersectionObserver; default keeps prior behaviour (bottom clipped 50px). */
	rootMargin?: string;
	/**
	 * IntersectionObserver visibility threshold(s). Default `[0.1]`; include `0` when the observed
	 * node can have ~zero layout size (e.g. wrapper around absolutely positioned children).
	 */
	intersectionThreshold?: number | number[];
	/**
	 * Tailwind translate class applied while hidden (pre-fade). Default `translate-y-8`.
	 * Use a smaller step (e.g. `translate-y-2`) for compact chrome like badges.
	 */
	hiddenTranslateClassName?: string;
	/**
	 * When set, this subtree is excluded from **sequential focus** (`inert`) until the reveal
	 * animation has been triggered. Use **only** for rare widgets (e.g. a large {@link TabGroup})
	 * where opacity-only hiding still leaves off-screen tabs in the tab order.
	 *
	 * Do **not** enable on stacked/nested marketing fades — that pattern caused widespread skipped
	 * tab stops (e.g. hero waitlist link) when combined with `display:contents` and staggered children.
	 */
	suppressTabUntilVisible?: boolean;
}

export function FadeInOnScroll({
	children,
	className = "",
	role,
	delay = 0,
	duration = 700,
	onFadeInComplete,
	immediate = false,
	rootMargin = "0px 0px -50px 0px",
	intersectionThreshold,
	hiddenTranslateClassName = "translate-y-8",
	suppressTabUntilVisible = false,
}: FadeInOnScrollProps) {
	const [isVisible, setIsVisible] = React.useState(immediate);
	const [isFadeInComplete, setIsFadeInComplete] = React.useState(immediate);
	const ref = React.useRef<HTMLDivElement>(null);
	const hasAnimated = React.useRef(immediate);
	const fadeInCompleteTimeoutRef = React.useRef<ReturnType<
		typeof setTimeout
	> | null>(null);

	// useLayoutEffect so above-the-fold nodes can flip `isVisible` before paint.
	React.useLayoutEffect(() => {
		if (immediate) {
			queueMicrotask(() => {
				onFadeInComplete?.();
			});
			return;
		}

		const element = ref.current;
		if (!element || hasAnimated.current) {
			return;
		}

		let observer: IntersectionObserver | null = null;
		let cancelled = false;
		let layoutRetryRaf = 0;
		let viewportFadeRaf = 0;

		const triggerFadeIn = () => {
			if (hasAnimated.current || cancelled) {
				return;
			}
			setIsVisible(true);
			hasAnimated.current = true;
			fadeInCompleteTimeoutRef.current = setTimeout(() => {
				setIsFadeInComplete(true);
				onFadeInComplete?.();
			}, duration + delay);
		};

		const attemptViewportFadeIn = () => {
			if (hasAnimated.current || cancelled) {
				return true;
			}
			const rect = element.getBoundingClientRect();
			const thinOrCollapsed =
				rect.width < 2 ||
				rect.height < 2 ||
				Number.isNaN(rect.width) ||
				Number.isNaN(rect.height);
			if (!thinOrCollapsed && !intersectsViewport(rect)) {
				return false;
			}
			if (
				thinOrCollapsed &&
				!(
					rect.bottom > 0 &&
					rect.right > 0 &&
					rect.left < window.innerWidth &&
					rect.top < window.innerHeight
				)
			) {
				return false;
			}
			viewportFadeRaf = requestAnimationFrame(() => {
				if (!cancelled) {
					triggerFadeIn();
				}
			});
			return true;
		};

		if (attemptViewportFadeIn()) {
			return () => {
				cancelled = true;
				cancelAnimationFrame(viewportFadeRaf);
				cancelAnimationFrame(layoutRetryRaf);
				if (fadeInCompleteTimeoutRef.current) {
					clearTimeout(fadeInCompleteTimeoutRef.current);
				}
				observer?.disconnect();
			};
		}

		layoutRetryRaf = requestAnimationFrame(() => {
			if (cancelled || hasAnimated.current) {
				return;
			}
			if (attemptViewportFadeIn()) {
				return;
			}
			const rect = element.getBoundingClientRect();
			const thinOrCollapsed =
				rect.width < 2 ||
				rect.height < 2 ||
				Number.isNaN(rect.width) ||
				Number.isNaN(rect.height);
			const threshold =
				intersectionThreshold ?? (thinOrCollapsed ? 0 : 0.1);
			const thresholdList = Array.isArray(threshold)
				? threshold
				: [threshold];
			observer = new IntersectionObserver(
				([entry]) => {
					if (entry?.isIntersecting && !hasAnimated.current) {
						triggerFadeIn();
						observer?.disconnect();
					}
				},
				{
					threshold:
						thresholdList.length > 0 ? thresholdList : [0, 0.1],
					rootMargin,
				},
			);
			observer.observe(element);
		});

		return () => {
			cancelled = true;
			cancelAnimationFrame(viewportFadeRaf);
			cancelAnimationFrame(layoutRetryRaf);
			observer?.disconnect();
			if (fadeInCompleteTimeoutRef.current) {
				clearTimeout(fadeInCompleteTimeoutRef.current);
			}
		};
	}, [
		delay,
		duration,
		immediate,
		intersectionThreshold,
		onFadeInComplete,
		rootMargin,
	]);

	const sequentialFocusSuppressed =
		Boolean(suppressTabUntilVisible) && !immediate && !isVisible;

	return (
		<FadeInContext.Provider value={{ isFadeInComplete }}>
			<div
				ref={ref}
				role={role}
				inert={sequentialFocusSuppressed ? true : undefined}
				className={`${className} transition-all ease-out ${
					isVisible
						? "translate-y-0 opacity-100"
						: `${hiddenTranslateClassName} opacity-0`
				}`}
				style={{
					transitionDuration: `${duration}ms`,
					transitionDelay: isVisible ? `${delay}ms` : "0ms",
				}}
			>
				{children}
			</div>
		</FadeInContext.Provider>
	);
}

export function useFadeInContext(): FadeInContextType {
	return React.useContext(FadeInContext);
}
